import { useState, useEffect } from 'react';
import { AuthService } from '../services/auth';
import { User, LoginCredentials, RegisterCredentials, AuthState } from '../types/auth';
import { setSentryUser, addBreadcrumb } from '../utils/sentryUtils';

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    error: null,
    isAuthenticated: false,
  });

  useEffect(() => {
    const unsubscribe = AuthService.onAuthStateChanged((user) => {
      // Only log authentication status, not user data
      if (process.env.NODE_ENV === 'development') {
        console.log('Auth state changed:', user ? 'User logged in' : 'User logged out');
      }
      
      // Update Sentry user context
      setSentryUser(user);
      
      // Add breadcrumb for auth state changes
      addBreadcrumb(
        user ? 'User authenticated' : 'User logged out',
        'auth',
        'info'
      );
      
      setAuthState({
        user,
        isLoading: false,
        error: null,
        isAuthenticated: !!user,
      });
    });

    return unsubscribe;
  }, []);

  const login = async (credentials: LoginCredentials) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const userCredential = await AuthService.login(credentials);
      const userDoc = await AuthService.getUserDocument(userCredential.user.uid);
      if (userDoc) {
        setSentryUser(userDoc);
        addBreadcrumb('User authenticated', 'auth', 'info');
      }
      setAuthState({
        user: userDoc,
        isLoading: false,
        error: null,
        isAuthenticated: true,
      });
      return userCredential;
    } catch (error) {
      const friendlyError = AuthService.handleAuthError(error, 'login');
      // Log error message only, not full error object which may contain sensitive data
      if (process.env.NODE_ENV === 'development') {
        console.error('Login error:', friendlyError.message);
      }
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: friendlyError.message,
      }));
      throw friendlyError;
    }
  };

  const register = async (credentials: RegisterCredentials, invitationToken?: string) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const userCredential = await AuthService.register(credentials, invitationToken);
      const userDoc = await AuthService.getUserDocument(userCredential.user.uid);
      if (userDoc) {
        setSentryUser(userDoc);
        addBreadcrumb('User authenticated', 'auth', 'info');
      }
      setAuthState({
        user: userDoc,
        isLoading: false,
        error: null,
        isAuthenticated: true,
      });
      return userCredential;
    } catch (error) {
      const friendlyError = AuthService.handleAuthError(error, 'register');
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: friendlyError.message,
      }));
      throw friendlyError;
    }
  };

  const logout = async () => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      await AuthService.logout();
      setAuthState({
        user: null,
        isLoading: false,
        error: null,
        isAuthenticated: false,
      });
    } catch (error) {
      const friendlyError = AuthService.handleAuthError(error, 'logout');
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: friendlyError.message,
      }));
      throw friendlyError;
    }
  };

  const resetPassword = async (email: string) => {
    setAuthState(prev => ({ ...prev, error: null }));
    
    try {
      await AuthService.resetPassword(email);
    } catch (error) {
      const friendlyError = AuthService.handleAuthError(error, 'reset-password');
      setAuthState(prev => ({
        ...prev,
        error: friendlyError.message,
      }));
      throw friendlyError;
    }
  };

  const updateProfile = async (updates: Partial<User>) => {
    if (!authState.user) return;
    
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      await AuthService.updateUserProfile(authState.user.id, updates);
      // Refresh user data
      const updatedUser = await AuthService.getUserDocument(authState.user.id);
      setAuthState(prev => ({
        ...prev,
        user: updatedUser,
        isLoading: false,
      }));
    } catch (error) {
      const friendlyError = AuthService.handleAuthError(error, 'update-profile');
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: friendlyError.message,
      }));
      throw friendlyError;
    }
  };

  const clearError = () => {
    setAuthState(prev => ({ ...prev, error: null }));
  };

  return {
    ...authState,
    login,
    register,
    logout,
    resetPassword,
    updateProfile,
    clearError,
  };
};